import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Ticket, Tag, Calendar, DollarSign, Percent,
  CheckCircle, XCircle, Clock, TrendingUp, Users, Edit, Loader,
  AlertTriangle, Copy, Eye
} from 'lucide-react';
import adminService, { CouponDetailsResponse, Coupon } from '../../services/admin/adminService';
import toast from 'react-hot-toast';

const CouponDetails: React.FC = () => {
  const { couponId } = useParams<{ couponId: string }>();
  const navigate = useNavigate();

  const [couponDetails, setCouponDetails] = useState<CouponDetailsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (couponId) {
      fetchCouponDetails();
    }
  }, [couponId]);

  const fetchCouponDetails = async () => {
    try {
      setLoading(true);
      const data = await adminService.getCouponById(Number(couponId));
      setCouponDetails(data);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch coupon details');
      navigate('/admin/coupons');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!couponDetails?.coupon) return;

    try {
      setProcessing(true);
      const coupon = couponDetails.coupon;
      const validFrom = new Date(coupon.valid_from);
      const validUntil = new Date(coupon.valid_until);
      
      await adminService.updateCoupon({
        coupon_id: coupon.coupon_id,
        coupon_code: coupon.coupon_code,
        offer_id: coupon.offer_id,
        discount_type: coupon.discount_type,
        discount_value: parseFloat(coupon.discount_value),
        min_purchase_amount: coupon.min_purchase_amount ? parseFloat(coupon.min_purchase_amount) : undefined,
        max_discount_amount: coupon.max_discount_amount ? parseFloat(coupon.max_discount_amount) : undefined,
        max_usage: coupon.max_usage || undefined,
        max_usage_per_user: coupon.max_usage_per_user || undefined,
        valid_from: validFrom.toISOString().slice(0, 16),
        valid_until: validUntil.toISOString().slice(0, 16),
        is_active: coupon.is_active === 0,
        description: coupon.description || '',
      });
      toast.success(`Coupon ${coupon.is_active === 0 ? 'activated' : 'deactivated'} successfully!`);
      fetchCouponDetails();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to toggle coupon status');
    } finally {
      setProcessing(false);
    }
  };

  const handleCopyCode = () => {
    if (couponDetails?.coupon) {
      navigator.clipboard.writeText(couponDetails.coupon.coupon_code);
      toast.success('Coupon code copied to clipboard!');
    }
  };

  const getStatusBadge = (coupon: Coupon) => {
    const now = new Date();
    const validFrom = new Date(coupon.valid_from);
    const validUntil = new Date(coupon.valid_until);
    
    if (coupon.is_active === 0) {
      return (
        <span className="px-3 py-1 bg-secondary-100 text-secondary-700 rounded-full text-sm font-semibold">
          Inactive
        </span>
      );
    }
    if (now < validFrom) {
      return (
        <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">
          Scheduled
        </span>
      );
    }
    if (now > validUntil) {
      return (
        <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-semibold">
          Expired
        </span>
      );
    }
    return (
      <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
        Active
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount: string | number) => {
    return `$${parseFloat(String(amount)).toLocaleString('en-US', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    })}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader className="animate-spin text-primary-500" size={48} />
      </div>
    );
  }

  if (!couponDetails || !couponDetails.coupon) {
    return (
      <div className="text-center py-20">
        <AlertTriangle size={64} className="text-secondary-300 mx-auto mb-4" />
        <p className="text-secondary-600 font-semibold mb-2">Coupon not found</p>
        <button
          onClick={() => navigate('/admin/coupons')}
          className="bg-primary-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-600 transition-colors"
        >
          Back to Coupons
        </button>
      </div>
    );
  }

  const coupon = couponDetails.coupon;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/admin/coupons')}
            className="bg-secondary-50 hover:bg-secondary-100 text-secondary-700 p-2 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-secondary-900">Coupon Details</h1>
            <p className="text-secondary-600 mt-1">View and manage coupon information</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate(`/admin/coupons/${coupon.coupon_id}/edit`)}
            className="bg-warning-50 hover:bg-warning-100 text-warning-600 px-4 py-2 rounded-lg font-semibold flex items-center space-x-2 transition-colors"
          >
            <Edit size={18} />
            <span>Edit Coupon</span>
          </button>
          <button
            onClick={handleToggleStatus}
            disabled={processing}
            className={`px-4 py-2 rounded-lg font-semibold flex items-center space-x-2 transition-colors disabled:opacity-50 ${
              coupon.is_active === 1
                ? 'bg-danger-50 hover:bg-danger-100 text-danger-600'
                : 'bg-success-50 hover:bg-success-100 text-success-600'
            }`}
          >
            {processing ? (
              <Loader className="animate-spin" size={18} />
            ) : coupon.is_active === 1 ? (
              <>
                <XCircle size={18} />
                <span>Deactivate</span>
              </>
            ) : (
              <>
                <CheckCircle size={18} />
                <span>Activate</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Coupon Code Card */}
      <div className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl shadow-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm opacity-90 mb-2">Coupon Code</p>
            <div className="flex items-center space-x-3">
              <h2 className="text-3xl font-bold">{coupon.coupon_code}</h2>
              <button
                onClick={handleCopyCode}
                className="bg-white/20 hover:bg-white/30 p-2 rounded-lg transition-colors"
                title="Copy Code"
              >
                <Copy size={18} />
              </button>
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm opacity-90 mb-2">Status</p>
            {getStatusBadge(coupon)}
          </div>
        </div>
      </div>

      {/* Main Details */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <div className="bg-white rounded-xl shadow-soft p-6">
            <h2 className="text-xl font-bold text-secondary-900 mb-6 flex items-center space-x-2">
              <Ticket className="text-primary-500" size={24} />
              <span>Basic Information</span>
            </h2>
            <div className="space-y-4">
              {coupon.description && (
                <div>
                  <label className="text-sm font-semibold text-secondary-600">Description</label>
                  <p className="text-secondary-700 mt-1">{coupon.description}</p>
                </div>
              )}
              {coupon.offer_title && (
                <div>
                  <label className="text-sm font-semibold text-secondary-600">Associated Offer</label>
                  <div className="flex items-center space-x-2 mt-1">
                    <Tag className="text-primary-500" size={16} />
                    <p className="text-secondary-900 font-semibold">{coupon.offer_title}</p>
                  </div>
                  {coupon.offer_description && (
                    <p className="text-sm text-secondary-600 mt-1">{coupon.offer_description}</p>
                  )}
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-semibold text-secondary-600">Discount Type</label>
                  <p className="text-secondary-900 mt-1 capitalize">{coupon.discount_type}</p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-secondary-600">Coupon ID</label>
                  <p className="text-secondary-900 mt-1">#{coupon.coupon_id}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Discount Details */}
          <div className="bg-white rounded-xl shadow-soft p-6">
            <h2 className="text-xl font-bold text-secondary-900 mb-6 flex items-center space-x-2">
              <DollarSign className="text-success-500" size={24} />
              <span>Discount Details</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Percent className="text-primary-600" size={20} />
                  <span className="text-sm font-semibold text-primary-700">Discount Value</span>
                </div>
                <p className="text-2xl font-bold text-primary-900">
                  {coupon.discount_type === 'percentage' 
                    ? `${coupon.discount_value}%` 
                    : formatCurrency(coupon.discount_value)}
                </p>
              </div>
              {coupon.min_purchase_amount && (
                <div className="bg-gradient-to-br from-warning-50 to-warning-100 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <TrendingUp className="text-warning-600" size={20} />
                    <span className="text-sm font-semibold text-warning-700">Min Purchase</span>
                  </div>
                  <p className="text-2xl font-bold text-warning-900">
                    {formatCurrency(coupon.min_purchase_amount)}
                  </p>
                </div>
              )}
              {coupon.max_discount_amount && (
                <div className="bg-gradient-to-br from-info-50 to-info-100 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <DollarSign className="text-info-600" size={20} />
                    <span className="text-sm font-semibold text-info-700">Max Discount</span>
                  </div>
                  <p className="text-2xl font-bold text-info-900">
                    {formatCurrency(coupon.max_discount_amount)}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Validity & Usage */}
          <div className="bg-white rounded-xl shadow-soft p-6">
            <h2 className="text-xl font-bold text-secondary-900 mb-6 flex items-center space-x-2">
              <Calendar className="text-info-500" size={24} />
              <span>Validity & Usage</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-sm font-semibold text-secondary-600">Valid From</label>
                <p className="text-secondary-900 mt-1 flex items-center space-x-2">
                  <Calendar size={16} className="text-secondary-400" />
                  <span>{formatDate(coupon.valid_from)}</span>
                </p>
              </div>
              <div>
                <label className="text-sm font-semibold text-secondary-600">Valid Until</label>
                <p className="text-secondary-900 mt-1 flex items-center space-x-2">
                  <Calendar size={16} className="text-secondary-400" />
                  <span>{formatDate(coupon.valid_until)}</span>
                </p>
              </div>
              <div>
                <label className="text-sm font-semibold text-secondary-600">Max Usage</label>
                <p className="text-secondary-900 mt-1">
                  {coupon.max_usage ? coupon.max_usage.toLocaleString() : 'Unlimited'}
                </p>
              </div>
              <div>
                <label className="text-sm font-semibold text-secondary-600">Max Usage Per User</label>
                <p className="text-secondary-900 mt-1">
                  {coupon.max_usage_per_user ? coupon.max_usage_per_user.toLocaleString() : 'Unlimited'}
                </p>
              </div>
              <div>
                <label className="text-sm font-semibold text-secondary-600">Total Usage</label>
                <p className="text-secondary-900 mt-1 flex items-center space-x-2">
                  <Users size={16} className="text-primary-500" />
                  <span className="font-semibold">{coupon.total_usage || coupon.usage_count || 0}</span>
                </p>
              </div>
              <div>
                <label className="text-sm font-semibold text-secondary-600">Remaining Usage</label>
                <p className="text-secondary-900 mt-1">
                  {coupon.max_usage 
                    ? (coupon.max_usage - (coupon.total_usage || coupon.usage_count || 0)).toLocaleString()
                    : 'Unlimited'}
                </p>
              </div>
            </div>
          </div>

          {/* Usage Statistics */}
          {couponDetails.usage_stats && couponDetails.usage_stats.length > 0 && (
            <div className="bg-white rounded-xl shadow-soft p-6">
              <h2 className="text-xl font-bold text-secondary-900 mb-6 flex items-center space-x-2">
                <TrendingUp className="text-success-500" size={24} />
                <span>Usage Statistics</span>
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-secondary-50">
                    <tr>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-secondary-900">Date</th>
                      <th className="text-center px-4 py-3 text-sm font-semibold text-secondary-900">Usage Count</th>
                      <th className="text-center px-4 py-3 text-sm font-semibold text-secondary-900">Unique Users</th>
                      <th className="text-right px-4 py-3 text-sm font-semibold text-secondary-900">Total Discount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {couponDetails.usage_stats.map((stat, index) => (
                      <tr key={index} className="hover:bg-secondary-50">
                        <td className="px-4 py-3 text-sm text-secondary-700">
                          {new Date(stat.usage_date).toLocaleDateString('en-IN')}
                        </td>
                        <td className="px-4 py-3 text-center text-sm text-secondary-700">
                          {stat.usage_count}
                        </td>
                        <td className="px-4 py-3 text-center text-sm text-secondary-700">
                          {stat.unique_users}
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-semibold text-secondary-900">
                          {formatCurrency(stat.total_discount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Sidebar */}
        <div className="space-y-6">
          {/* Timestamps */}
          <div className="bg-white rounded-xl shadow-soft p-6">
            <h2 className="text-lg font-bold text-secondary-900 mb-4">Timestamps</h2>
            <div className="space-y-3">
              <div>
                <p className="text-xs text-secondary-600">Created At</p>
                <p className="text-sm text-secondary-900 mt-1">{formatDate(coupon.created_at)}</p>
              </div>
              <div>
                <p className="text-xs text-secondary-600">Last Updated</p>
                <p className="text-sm text-secondary-900 mt-1">{formatDate(coupon.updated_at)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CouponDetails;

